import { Alert, Button, Message, Spin } from "@arco-design/web-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { fetchCustomizationDemand } from "../repo/customizationRepo";
import {
  buildDemandExcelDetailRows,
  buildDemandExcelDetailTitle,
} from "../service/buildDemandExcelDetailRows";
import {
  demandExcelDetailImageFileName,
  downloadDemandExcelDetailImage,
} from "../service/downloadDemandExcelDetailImage";
import type {
  CustomizationDemandVersionOption,
  CustomizationProjectListItem,
} from "../types";

import "./CustomizationDemandExcelDetail.css";

type ProjectContext = Pick<
  CustomizationProjectListItem,
  | "name"
  | "projectCode"
  | "legacyId"
  | "customerType"
  | "tripType"
  | "signType"
  | "dealProgress"
  | "salesUserName"
  | "dispatcherUserName"
  | "operatingCompany"
  | "organizationNameSnapshot"
  | "customerName"
  | "contactName"
  | "projectCreatedAt"
>;

type Props = {
  projectId: string;
  version: CustomizationDemandVersionOption;
  projectContext: ProjectContext;
  view?: "workbench" | "preview";
};

/** 展示工作台需求定制全部表单参数的只读详细表。 */
export function CustomizationDemandExcelDetailPanel({
  projectId,
  version,
  projectContext,
  view = "preview",
}: Props) {
  const [downloading, setDownloading] = useState(false);
  const query = useQuery({
    queryKey: [
      "customization",
      "projects",
      projectId,
      "demands",
      version.id,
      "excel-detail",
      view,
    ],
    queryFn: () => fetchCustomizationDemand(projectId, version.id, { view }),
  });

  if (query.isLoading || query.isFetching) {
    return <Spin loading style={{ width: "100%", minHeight: 120 }} />;
  }

  if (query.error || !query.data) {
    return <Alert type="error" showIcon content="需求版本详细加载失败，请稍后重试。" />;
  }

  const rows = buildDemandExcelDetailRows({
    demand: query.data,
    version,
    projectContext,
  });
  const title = buildDemandExcelDetailTitle({
    orderVersionKey: version.orderVersionKey,
    projectCode: projectContext.projectCode,
    legacyId: projectContext.legacyId,
    versionSeq: version.versionSeq,
    opportunityName: projectContext.name,
  });

  /** 把当前详表画成 PNG 并交给浏览器下载。 */
  async function handleDownloadImage() {
    setDownloading(true);
    try {
      await downloadDemandExcelDetailImage({
        title,
        rows,
        fileName: demandExcelDetailImageFileName(
          version.orderVersionKey || projectContext.projectCode || title,
        ),
      });
    } catch {
      Message.error("详表图片下载失败，请稍后重试。");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="customization-demand-excel-detail" aria-label="需求版本 Excel 详细">
      <div className="customization-demand-excel-toolbar">
        <Button
          type="primary"
          size="small"
          loading={downloading}
          onClick={() => void handleDownloadImage()}
        >
          下载图片
        </Button>
      </div>
      <div className="customization-demand-excel-title">{title}</div>
      <dl className="customization-demand-excel-grid">
        {rows.map((row) => {
          const value = row.value || "--";
          return (
            <div key={row.key ?? row.label} className="customization-demand-excel-item">
              <dt className="customization-demand-excel-name">{row.label}</dt>
              <dd
                className={
                  row.changed
                    ? "customization-demand-excel-value is-changed"
                    : "customization-demand-excel-value"
                }
              >
                {value}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
